// 09A · Inicio: header con campana, saludo, ticker, motor compacto,
// "En vivo ahora" (carrusel .lv), "Próximos hoy" (máx. 3 + Agenda),
// "Señales activas" (+ "Señales enviadas hoy"), "Últimos 10 días".
// Estados 15.1 (sin acción) y 16.1/16.2 (offline con y sin caché).
// Fuentes: /live_games + /picks (useLiveV3), /signals_history
// (useClientStats), /radar_vip_results (useRadarResults), /today_games.
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthContext';
import useClientStats from '../../hooks/useClientStats';
import useRadarResults, { santiagoDayRadar } from '../../../hooks/useRadarResults';
import { santiagoDateStr, santiagoMidnightToUTCms, shiftDays, statsOfSubset } from '../../../components/PerformanceLab';
import { matchOf, pickOf } from '../../components/SignalCard';
import { HdBar, Ticker, MotorA, LvCard, SigCard, UpRow, Streak, NoAccess } from '../cards';
import { EmptyInline, EmptyFull, NextUp, OffBar, I, SigSkeleton } from '../ui';
import { useTodayGames, useReminders, useOnline, ventanaDe } from '../hooks';
import { useLiveV3, fmtNum, gidDeSenal, shortName } from '../live';

function fechaHoyChile() {
  const s = new Intl.DateTimeFormat('es-CL', { timeZone: 'America/Santiago', weekday: 'long', day: 'numeric', month: 'long' }).format(new Date());
  return s.charAt(0).toUpperCase() + s.slice(1);
}
const esHbSig = (s) => s.sport === 'handball' || s.match_type === 'handball' || /handball|hbl|ehf|asobal|starligue|handbold|handboll/i.test(String(s.league || s.liga || ''));

export default function Inicio() {
  const navigate = useNavigate();
  const { userData, user, hasActiveAccess, isAdmin } = useAuth();
  const acceso = hasActiveAccess || isAdmin;
  const { live, loading: lLive } = useLiveV3();
  const { signals, hoy, activasHoy, days, loading: lSig } = useClientStats();
  const { alerts } = useRadarResults();
  const hoyStr = santiagoDateStr(new Date());
  const { games: agenda } = useTodayGames(hoyStr);
  const { reminders, toggle } = useReminders();
  const { online, sinceStr } = useOnline();

  const nombre = (userData?.nombre || user?.displayName || '').trim().split(/\s+/)[0] || 'crack';
  const nEntrar = live.filter((l) => l.estado === 'ENTRAR').length;
  const nVigilar = live.filter((l) => l.estado === 'VIGILAR').length;
  const radarHoy = useMemo(() => alerts.filter((a) => a.fecha === santiagoDayRadar(new Date())), [alerts]);
  const proximos = useMemo(() => agenda.filter((g) => g.estado === 'proximo' && g.cobertura).slice(0, 3), [agenda]);
  const nextUp = proximos[0] || null;

  // Señales activas: emitidas hoy sin resultado + alertas RADAR de hoy en curso
  const activas = useMemo(() => [
    ...activasHoy.map((s) => ({ key: `s_${s.id}`, ts: s.started_at || 0, tipo: 'live', s })),
    ...radarHoy.filter((a) => !a.resolved).map((a) => ({ key: `r_${a.id}`, ts: a.ts, tipo: 'radar', a })),
  ].sort((x, y) => y.ts - x.ts), [activasHoy, radarHoy]);

  // Resumen de ayer para el vacío de "Señales activas"
  const ayer = useMemo(() => {
    const d = shiftDays(hoyStr, -1); const a = santiagoMidnightToUTCms(d), b = santiagoMidnightToUTCms(hoyStr);
    return statsOfSubset(signals.filter((s) => (s.started_at || 0) >= a && (s.started_at || 0) < b));
  }, [signals, hoyStr]);

  // Racha: aciertos seguidos contando desde la señal cerrada más reciente
  const racha = useMemo(() => { let n = 0; for (const s of signals) { if (s.result === 'WIN') n++; else if (s.result === 'LOSS') break; } return n; }, [signals]);

  const ticker = useMemo(() => {
    const it = [];
    for (const l of live) {
      if (l.marcador.away == null) continue;
      it.push({ dot: '', a: shortName(l.away), sc: <><b>{l.marcador.away}</b>–<b>{l.marcador.home}</b></>, b: shortName(l.home), sc2: l.tiempo.split(' · ')[0], ed: l.edge != null ? fmtNum(l.edge) : null });
    }
    for (const s of hoy.filter((x) => x.result === 'WIN' || x.result === 'LOSS').slice(0, 4)) {
      it.push({ dk: true, dot: s.result === 'WIN' ? 'g' : 'w', a: matchOf(s).split(' vs ').map(shortName).join(' vs '), sc: pickOf(s), ed: s.result, edCls: s.result === 'WIN' ? 'g' : 'n' });
    }
    for (const l of live.filter((x) => x.estado === 'VIGILAR')) it.push({ dot: 'w', a: 'Vigilar', sc: `${l.liga} · umbral en ${l.falta.toFixed(1)}` });
    if (racha >= 2) it.push({ dk: true, dot: 'i', a: 'Racha', sc: <><b>{racha}</b> aciertos seguidos</> });
    if (live.length) it.push({ dot: 'g', a: 'Motor', sc: <><b>{live.length}</b> partido{live.length === 1 ? '' : 's'} en análisis</> });
    return it;
  }, [live, hoy, racha]);

  const sinCache = !online && lLive && lSig;
  const liveVisibles = acceso ? live : live.slice(0, 2);
  const activasVisibles = acceso ? activas : activas.slice(0, 2);

  return (
    <div className="v3"><div className="scroll safe-top">
      <HdBar nombre={nombre} />
      {!online && !sinCache && <OffBar since={sinceStr} />}
      {sinCache ? (
        <EmptyFull icon={<I.wifiOff />} title="Sin conexión" cta="Reintentar" onCta={() => window.location.reload()}>
          No pudimos cargar las señales del día. Revisa tu red; reintentamos automáticamente cuando vuelva.
        </EmptyFull>
      ) : (
        <>
          <div className="greet"><p className="k">{fechaHoyChile()}</p><h1>Hola, {nombre}</h1></div>
          <div className={online ? '' : 'stale'}>
            {online && <Ticker items={ticker} />}
            <MotorA nLive={live.length} nEntrar={nEntrar} nVigilar={nVigilar} offline={!online} />

            <div className="sh"><h2>En vivo ahora</h2>{live.length > 0 && <a href="/app/senales" onClick={(e) => { e.preventDefault(); navigate('/app/senales'); }}>Ver todos</a>}</div>
            {lLive ? <SigSkeleton /> : live.length === 0 ? (
              <>
                <EmptyInline icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6E6E73" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>} title="Nada en juego ahora">
                  {nextUp ? <>El próximo partido con cobertura empieza a las <b style={{ color: 'var(--ink)' }}>{nextUp.hora}</b>. Te avisamos si el motor emite.</> : <>No hay partidos con cobertura en juego. Te avisamos si el motor emite.</>}
                </EmptyInline>
                {nextUp && <NextUp emoji={nextUp.esHandball ? '🤾' : '🏀'} liga={nextUp.liga} partido={`${nextUp.home} vs ${nextUp.away}`} hora={nextUp.hora} />}
              </>
            ) : (
              <>
                <div className="live-strip">{liveVisibles.map((l) => <LvCard key={l.gid} l={l} />)}</div>
                {!acceso && live.length > 2 && <NoAccess que={`Hay ${live.length - 2} partido${live.length - 2 === 1 ? '' : 's'} más en análisis.`} />}
              </>
            )}

            {proximos.length > 0 && (
              <>
                <div className="sh"><h2>Próximos hoy</h2><a href="/app/agenda" onClick={(e) => { e.preventDefault(); navigate('/app/agenda'); }}>Agenda</a></div>
                {proximos.map((g) => <UpRow key={g.gid} g={g} esHoy on={!!reminders[g.gid]} onBell={toggle} fecha={hoyStr} />)}
              </>
            )}

            <div className="sh"><h2>Señales activas</h2><a href="/app/senales?ver=hoy" onClick={(e) => { e.preventDefault(); navigate('/app/senales?ver=hoy'); }}>Señales enviadas hoy</a></div>
            {lSig ? <SigSkeleton /> : activas.length === 0 ? (
              <EmptyInline title="Sin señales activas" cta="Ver historial" ghost onCta={() => navigate('/app/historial')}>
                {ayer.decided > 0 ? <>Ayer cerraste <b style={{ color: 'var(--pos)' }}>{ayer.wins} de {ayer.decided}</b>. </> : null}El motor solo emite cuando el edge supera el umbral.
              </EmptyInline>
            ) : (
              <>
                {activasVisibles.map((x) => x.tipo === 'live' ? (
                  <SigCard key={x.key} liga={x.s.league || x.s.liga || '—'} chip={{ kind: 'sport', txt: esHbSig(x.s) ? '🤾 Handball' : '🏀 Básquet' }}
                    edge={x.s.edge_initial ?? x.s.edge} away={matchOf(x.s).split(' vs ')[0]} home={matchOf(x.s).split(' vs ')[1] || ''}
                    cols={[{ l: 'Pick', v: pickOf(x.s), cls: 'g' }, { l: 'Ventana', v: ventanaDe(x.s) }, { l: 'Resultado', v: 'En juego', res: 'p' }]}
                    onClick={() => navigate(`/app/partido/${gidDeSenal(x.s)}`, { state: { signal: x.s } })} />
                ) : (
                  <SigCard key={x.key} liga={x.a.liga} chip={{ kind: 'sport', txt: '📡 RADAR' }} edge={x.a.edge}
                    away={x.a.partido.split(' @ ')[0]} home={x.a.partido.split(' @ ')[1] || ''}
                    cols={[{ l: 'Pick', v: `${x.a.side} ${x.a.linea ?? ''}`, cls: 'g' }, { l: 'Ventana', v: x.a.ventana }, { l: 'Resultado', v: 'En juego', res: 'p' }]}
                    onClick={() => navigate(`/app/partido/${x.a.gid}`)} />
                ))}
                {!acceso && activas.length > 2 && <NoAccess que={`Hay ${activas.length - 2} señal${activas.length - 2 === 1 ? '' : 'es'} más activa${activas.length - 2 === 1 ? '' : 's'} hoy.`} />}
              </>
            )}

            <div className="sh"><h2>Últimos 10 días</h2><a href="/app/historial" onClick={(e) => { e.preventDefault(); navigate('/app/historial'); }}>Historial</a></div>
            <Streak days={days} />
          </div>
        </>
      )}
    </div></div>
  );
}
